import type Plotly from "plotly.js-dist-min"

/**
 * Indice 0 = dimanche … 6 = samedi (aligné sur les séries API 7 pts / 168 pts).
 */
export const ALARM_THRESHOLD_WEEKDAY_LABELS_FR = [
    "Dimanche",
    "Lundi",
    "Mardi",
    "Mercredi",
    "Jeudi",
    "Vendredi",
    "Samedi",
] as const

/**
 * Indices de minuit (00h00) pour chaque jour sur une grille horaire 7×24 (0…167).
 */
export const ALARM_THRESHOLD_HOURLY_DAY_START_INDICES = [
    0, 24, 48, 72, 96, 120, 144,
] as const

export function inferAlarmThresholdFrequency(
    pointCount: number
): "hourly" | "daily" | undefined {
    if (pointCount === 7) return "daily"
    if (pointCount === 168) return "hourly"
    return undefined
}

/**
 * Borne supérieure d’axe Y : max des séries + 20 % (échelle lisible pour les petites valeurs, ex. kWh).
 *
 * @param chartData traces Plotly (consigne / limite haute / basse)
 * @returns max Y à passer en `layout.yaxis.range[1]` (minimum 1 si tout est nul)
 */
export function computeAlarmThresholdYAxisUpperBound(
    chartData: Plotly.Data[]
): number {
    let max = 0
    for (const trace of chartData) {
        if (!trace || typeof trace !== "object" || !("y" in trace)) continue
        const y = (trace as { y?: unknown }).y
        if (!Array.isArray(y)) continue
        for (const v of y) {
            const n = typeof v === "number" ? v : Number(v)
            if (Number.isFinite(n) && n > max) max = n
        }
    }
    if (max <= 0) return 1
    return max * 1.2
}

function weekdayLabelFromIndex(dayIndex: number): string {
    const i = Math.min(6, Math.max(0, Math.floor(dayIndex)))
    return ALARM_THRESHOLD_WEEKDAY_LABELS_FR[i] ?? String(dayIndex)
}

function hourlyPointHoverLabel(index: number): string {
    const day = Math.floor(index / 24)
    const hour = index % 24
    return `${weekdayLabelFromIndex(day)} · ${String(hour).padStart(2, "0")}h00`
}

/**
 * Infobulles avec unité sur l’axe Y ; axe X en jours (quotidien) ou contexte jour+heure (horaire).
 */
export function enrichAlarmThresholdChartData(
    chartData: Plotly.Data[],
    frequency: "hourly" | "daily" | undefined,
    yAxisUnit?: string
): Plotly.Data[] {
    const unit = yAxisUnit?.trim()
    const yPart = unit ? `%{y:.2f} ${unit}` : "%{y:.2f}"
    const baseHover = `<b>%{fullData.name}</b><br>${yPart}<extra></extra>`

    if (!chartData.length) {
        return chartData
    }

    const firstTrace = chartData[0]
    const x0 = firstTrace?.x
    if (!Array.isArray(x0) || x0.length === 0) {
        return chartData.map((t) => ({ ...t, hovertemplate: baseHover }))
    }

    if (!frequency) {
        return chartData.map((t) => ({ ...t, hovertemplate: baseHover }))
    }

    if (frequency === "daily") {
        return chartData.map((t) => ({
            ...t,
            x: Array.isArray(t.x)
                ? (t.x as unknown[]).map((v) =>
                      weekdayLabelFromIndex(Number(v))
                  )
                : t.x,
            hovertemplate: `<b>%{fullData.name}</b><br>%{x}<br>${yPart}<extra></extra>`,
        }))
    }

    return chartData.map((t) => ({
        ...t,
        hovertext: Array.isArray(t.x)
            ? (t.x as number[]).map((xi) => hourlyPointHoverLabel(Number(xi)))
            : undefined,
        hovertemplate: `<b>%{fullData.name}</b><br>%{hovertext}<br>${yPart}<extra></extra>`,
    }))
}
